import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { deriveStudioState, type StudioState } from '@/lib/studio/studioState';
import type { VideoOutline } from '@/types/outline';
import { studioInputFrom } from './studioInput';

/** What a checkpoint card can say about the video without asking anyone: scene count and length. */
export interface StudioSummary {
  scenes: number;
  durationSec?: number;
}

export const StudioCtx = createContext<StudioState | null>(null);
export const SummaryCtx = createContext<StudioSummary | null>(null);

/** The studio state for the workspace around this component, or null outside a video workspace. */
export const useStudio = () => useContext(StudioCtx);

export const useStudioSummary = () => useContext(SummaryCtx);

/** Derives the studio state from the workspace's chat and outline; recomputes as either changes. */
export function useStudioState(opts: {
  outline: VideoOutline | null;
  hasManifest: boolean;
  manifestLoading?: boolean;
  narration?: Parameters<typeof studioInputFrom>[0]['narration'];
}): StudioState {
  const messages = useEmbeddedIvyChatStore((s) => s.messages);
  const isStreaming = useEmbeddedIvyChatStore((s) => s.isStreaming);
  return useMemo(
    () => deriveStudioState(studioInputFrom({ outline: opts.outline, messages, isStreaming, hasManifest: opts.hasManifest, manifestLoading: opts.manifestLoading, narration: opts.narration })),
    [opts.outline, opts.hasManifest, opts.manifestLoading, opts.narration, messages, isStreaming],
  );
}

/** The current time, ticking once a second while `active`, for elapsed timers. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}
